"use client";

import { useState, useEffect, useRef, useSyncExternalStore } from "react";
import Image from "next/image";
import { NavArrowRight } from "iconoir-react";
import { BookTourButton } from "./BookTourButton";

type CallToAction = {
  label: string;
  href: string;
};

type TourHighlight = {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  imageAlt: string;
};

const highlights: TourHighlight[] = [
  {
    id: "food",
    title: "Food",
    description:
      "Eat where the neighborhood eats. Plates at Communion, fried chicken at Ezell's (an Oprah favorite), and a deep roster of Ethiopian kitchens around every corner.",
    imageUrl: "/photos/events_umojafest_5.jpg",
    imageAlt: "Food along the tour",
  },
  {
    id: "landmarks",
    title: "Landmarks",
    description:
      "See where Seattle's Black Panther Party began, where the Northwest's first Black bank opened, and the parks and schools that hold this neighborhood together.",
    imageUrl: "/photos/william_grose.jpg",
    imageAlt: "Landmarks along the tour",
  },
  {
    id: "history",
    title: "History",
    description:
      "Trace the roots. William Grose's homestead, the Jackson Street jazz era, and one of the longest act of civil disobedience in U.S. history.",
    imageUrl: "/photos/events_umojafest_3.jpg",
    imageAlt: "History along the tour",
  },
  {
    id: "community",
    title: "Community",
    description:
      "Walk into a living community. Umoja PEACE Center, neighborhood churches, summer cookouts, and gathering spaces where you arrive a visitor and leave family.",
    imageUrl: "/photos/africatown_plaza.jpg",
    imageAlt: "Community along the tour",
  },
  {
    id: "legacy",
    title: "Legacy",
    description:
      "Walk the blocks that shaped Ray Charles, Jimi Hendrix, Quincy Jones, and Macklemore. The same community is now resisting erasure to keep these stories alive.",
    imageUrl: "/photos/garfield_high_school.jpg",
    imageAlt: "Legacy along the tour",
  },
];

const subscribeHover = (callback: () => void) => {
  if (typeof window === "undefined") return () => {};
  const mediaQuery = window.matchMedia("(hover: hover)");
  mediaQuery.addEventListener("change", callback);
  return () => mediaQuery.removeEventListener("change", callback);
};

const getHoverSnapshot = () => {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(hover: hover)").matches;
};

const getServerHoverSnapshot = () => false;

export const TourHighlights = () => {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const isHoverDevice = useSyncExternalStore(
    subscribeHover,
    getHoverSnapshot,
    getServerHoverSnapshot
  );
  const activeHighlight = highlights[activeIndex];

  const mousePosRef = useRef({ x: 0, y: 0 });
  const lastActiveMousePosRef = useRef({ x: -1, y: -1 });

  function handleSelect(index: number) {
    setActiveIndex(index);
  }

  function handleMouseEnter(index: number) {
    if (!isHoverDevice) return;

    const currentPos = mousePosRef.current;
    const lastPos = lastActiveMousePosRef.current;

    if (currentPos.x === lastPos.x && currentPos.y === lastPos.y) {
      return;
    }

    setActiveIndex(index);
    lastActiveMousePosRef.current = { ...currentPos };
  }

  function handleNext() {
    setActiveIndex((i) => (i + 1) % highlights.length);
  }

  function handlePrev() {
    setActiveIndex((i) => (i - 1 + highlights.length) % highlights.length);
  }

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mousePosRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "ArrowLeft") handlePrev();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div
      className="tour-highlights w-full grid grid-cols-1 gap-lg md:grid-cols-[minmax(0,36rem)_1fr] md:grid-rows-[auto_1fr] items-start"
      aria-label="Tour highlights"
    >
      <ul
        className="listContainer flex flex-col gap-xs md:col-start-1 md:row-start-1"
        role="tablist"
        aria-orientation="vertical"
      >
        {highlights.map((highlight, index) => {
          const isActive = index === activeIndex;
          return (
            <li key={highlight.id}>
              <button
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-current={isActive ? "true" : undefined}
                className={`listItem text-left w-full text-3xl sm:text-[2.5rem] sm:leading-[2.75rem] font-display text-primary-dark px-md py-xs transition-opacity duration-200 ${
                  isActive ? "opacity-100" : "opacity-70"
                }`}
                style={
                  isActive
                    ? { textShadow: "0.06ex 0 0 currentColor" }
                    : undefined
                }
                onClick={() => handleSelect(index)}
              >
                <span
                  className="inline-flex items-center gap-sm"
                  onMouseEnter={() => handleMouseEnter(index)}
                >
                  <NavArrowRight
                    aria-hidden="true"
                    className={`indicator w-6 h-6 shrink-0 stroke-3 ${
                      isActive ? "visible" : "invisible"
                    }`}
                  />
                  {highlight.title}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      <div className="imageViewer relative z-0 flex items-center justify-center md:col-start-2 md:row-start-1 md:row-span-2 lg:-translate-x-[7rem] lg:-translate-y-4xl">
        <Image
          key={activeHighlight.id}
          src={activeHighlight.imageUrl}
          alt={activeHighlight.imageAlt}
          width={0}
          height={0}
          sizes="50vw"
          className="w-full h-full object-cover rounded-4xl"
        />
      </div>

      <div
        className="detailsPanel relative z-[1] flex flex-col xl:min-w-[25rem] gap-md p-xl bg-primary-dark text-sm text-white rounded-4xl sm:rounded-tl-none sm:ml-12 md:col-start-1 md:row-start-2"
        aria-live="polite"
      >
        {" "}
        {/* <h3 className="font-reem-kufi text-3xl">{activeHighlight.title}</h3> */}
        <p className="detailsText font-open-sans font-[300] sm:text-lg">
          {activeHighlight.description}
        </p>
        <BookTourButton variant="light" className="mt-auto" />
      </div>
    </div>
  );
};
